import { Theme, useTheme } from '@emotion/react'
import {
    isFeatureEnabled,
    toFeatureState,
} from '@west-australian-newspapers/etrigan-feature-toggles-client'
import {
    BaseClientConfig,
    CardEvents,
    Product,
    Topic,
    useToggleState,
    VideoQueueItemMeta,
    getVideoWatchPageUrl,
} from '@news-mono/web-common'
import { VideoMetaDTO } from '@west-australian-newspapers/publication-types'
import React from 'react'
import {
    AutoplayOptions,
    GetVideoQueue,
    VideoOptions,
} from '../../content/Video/PlayerInterface'
import { useGdprMode } from '../../content/Video/utils/gdpr-mode'
import { ArticleVideo } from '../../publication/ArticleVideo/ArticleVideo'
import {
    StyledVideoPlayer,
    LazyMountWrapper,
    VideoPlaceholder,
} from './VideoPlayer.styled'
import { mapPosterImageDtoToImageSource } from '../../content/Video/utils/mapPosterImageDto'
import { useViewport } from '../../__helpers/use-viewport'
import { useLazyLoading } from '../../__helpers/with-lazy-loading'

export interface VideoHubPlayerProps {
    onEvent: (event: CardEvents) => void
    item: VideoMetaDTO
    config: BaseClientConfig
    adUnitPath: string
    currentPath: string
    getVideoQueue: GetVideoQueue
    isVideoWatchPage?: boolean
    autoplayWhenInView?: boolean
    adElement?: JSX.Element
    lazyLoad?: boolean
}

const onPlayNextVideo = (nextVideo: VideoQueueItemMeta) => {
    if (nextVideo.topicMeta?.id?.includes('entertainment')) {
        const videoUrl = getVideoWatchPageUrl(
            nextVideo.topicMeta as Topic,
            nextVideo,
        )
        window && window.history.pushState(null, nextVideo.name ?? '', videoUrl)
    }
}

export const VideoPlayer: React.FC<VideoHubPlayerProps> = ({
    onEvent,
    config,
    item,
    adUnitPath,
    currentPath,
    getVideoQueue,
    isVideoWatchPage,
    autoplayWhenInView,
    adElement,
    lazyLoad = false,
}) => {
    const { isMobile } = useViewport()
    const toggles = useToggleState()
    const gdprMode = useGdprMode()
    const theme = useTheme()

    // Use shared lazy-loading hook (SSR-aware). Only when explicitly enabled.
    const { lazyLoadHasTriggered, lazyLoadRef } =
        useLazyLoading<HTMLDivElement>()
    const shouldUseLazy = lazyLoad === true
    const shouldRenderPlayer = shouldUseLazy ? lazyLoadHasTriggered : true
    const wrapperRef = shouldUseLazy ? lazyLoadRef : undefined

    const isVideoHubMobileAutoplayEnabled = isFeatureEnabled(
        toFeatureState(toggles),
        '7news-mobile-video-autoplay',
    )

    const shouldDisableAutoplay = (theme: Theme) => {
        if (!isMobile) {
            return false
        }

        // EDIT-663 & EDIT-729:
        // On mobile devices, we disable autoplay.
        // If we dont, the videohub will attempt to play a video, freeze playback and plaback controls will be hidden (?!?!?)
        // This bug does not affect 7News, as the current 7News implementation does not need to scroll into view.
        if (theme.kind === 'perthnow' || theme.kind === 'thewest') {
            return true
        }

        if (
            theme.kind === Product.SevenNews &&
            !isVideoHubMobileAutoplayEnabled
        ) {
            return true
        }

        return false
    }

    const autoplayOptions: false | AutoplayOptions = shouldDisableAutoplay(
        theme,
    )
        ? false
        : {
              delayDuration:
                  theme.kind === Product.SevenNews && !autoplayWhenInView
                      ? 3
                      : 0,
              //pause countdown still needs to be on for initial video load pause
              pauseCountdownOutsideViewport:
                  theme.kind === Product.SevenNews && !autoplayWhenInView
                      ? false
                      : true,
              pauseWhenOutOfView:
                  theme.kind === Product.SevenNews && !autoplayWhenInView
                      ? false
                      : true,
          }
    const autoplayNextOptions: false | AutoplayOptions = {
        delayDuration: 3,
        pauseCountdownOutsideViewport:
            theme.kind === Product.SevenNews && !autoplayWhenInView
                ? false
                : true,
        pauseWhenOutOfView:
            theme.kind === Product.SevenNews && !autoplayWhenInView
                ? false
                : true,
    }

    const videoOptions: VideoOptions = {
        adUnitPath,
        canAutoplayVideos: !shouldDisableAutoplay(theme),
        enableSnowplow: false,
        enablePrebid: isFeatureEnabled(toFeatureState(toggles), 'prebid-video'),
        isMagniteEnabled: isFeatureEnabled(
            toFeatureState(toggles),
            'magnite-header-tag-wrapper',
        ),
        gdprMode,
    }

    return (
        <StyledVideoPlayer>
            <LazyMountWrapper
                ref={wrapperRef as React.RefObject<HTMLDivElement>}
                enable={shouldUseLazy && !shouldRenderPlayer}
            >
                {shouldRenderPlayer ? (
                    <ArticleVideo
                        videoDetails={{
                            id: item.id,
                            accountId: item.accountId,
                            heading: String(item.name),
                            posterImage: mapPosterImageDtoToImageSource(
                                item.posterImage,
                            ),
                            videoType: item.videoType as 'vod' | 'live' | null,
                        }}
                        showTitles={false}
                        autoplayOptions={autoplayOptions}
                        autoplayNextOptions={autoplayNextOptions}
                        videoOptions={videoOptions}
                        pauseOverlay={false}
                        isVideoStickyEnabled={false}
                        onEvent={onEvent}
                        getVideoQueue={getVideoQueue}
                        showPlaylist={true}
                        publicationPathname={currentPath}
                        isHeroVideo={true}
                        isVideoHub={true}
                        publicUrl={config.publicUrl}
                        onPlayNextVideo={
                            isVideoWatchPage &&
                            item.topicMeta?.id.includes('entertainment')
                                ? onPlayNextVideo
                                : undefined
                        }
                        adElement={adElement}
                    />
                ) : (
                    <VideoPlaceholder />
                )}
            </LazyMountWrapper>
        </StyledVideoPlayer>
    )
}
